import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'ui/lib/utils';
import { LoaderCircle } from 'lucide-react';

const buttonVariants = cva(
	'inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap rounded-sm text-sm font-medium transition-colors focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 font-lato',
	{
		variants: {
			variant: {
				default: 'bg-primary text-primary-foreground hover:bg-primary/90',
				destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
				outline: 'border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
				secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
				ghost: 'text-foreground hover:bg-accent hover:text-accent-foreground',
				link: 'text-primary underline-offset-4 hover:underline',
				icon: '[&_svg]:size-4'
			},
			size: {
				default: 'h-9 px-4 py-2',
				sm: 'h-8 rounded-sm px-3 text-xs',
				lg: 'h-10 rounded-sm px-8',
				icon: 'h-9 w-9'
			}
		},
		defaultVariants: {
			variant: 'default',
			size: 'default'
		}
	}
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
	asChild?: boolean;
	icon?: React.ReactNode;
	loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, icon, loading = false, ...props }, ref) => {
		return (
			<>
				{asChild ? (
					<Slot
						className={cn(buttonVariants({ variant, size, className }))}
						ref={ref}
						{...props}>
						{props.children}
					</Slot>
				) : (
					<button
						className={cn(buttonVariants({ variant, size, className }))}
						ref={ref}
						disabled={loading || props.disabled}
						{...props}>
						{loading ? <LoaderCircle className="w-4 h-4 animate-spin" /> : icon}
						{props.children}
					</button>
				)}
			</>
		);
	}
);
Button.displayName = 'Button';

export { Button };
